/**
 * WorkspaceContext
 *
 * Manages the current workspace (production vs inventory) with URL as source of truth.
 *
 * Architecture:
 * - Workspace is DERIVED from the current route pathname (not stored in state)
 * - localStorage stores preference for "last used workspace"
 * - On initial load at root '/', redirects to stored workspace preference
 * - Direct URL navigation (bookmarks) respects URL intent, no redirect
 * - Browser back/forward automatically updates workspace (derived from URL)
 *
 * This approach eliminates race conditions and respects user intent.
 */

import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type Workspace = 'production' | 'inventory';

interface WorkspaceContextType {
  workspace: Workspace;
  setWorkspace: (ws: Workspace) => void;
  transitioning: boolean;
}

const WorkspaceContext = createContext<WorkspaceContextType | null>(null);

const STORAGE_KEY = 'app_workspace';

// Route constants (single source of truth for workspace default routes)
const WORKSPACE_ROUTES = {
  production: '/production/dashboard',
  inventory: '/',
} as const;

/**
 * Derive workspace from pathname.
 * Any route starting with /production is the production workspace.
 */
function getWorkspaceFromPath(pathname: string): Workspace {
  return pathname.startsWith('/production') ? 'production' : 'inventory';
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [transitioning, setTransitioning] = useState(false);
  const [initialSyncDone, setInitialSyncDone] = useState(false);

  // Derive workspace from current route (URL is source of truth)
  const workspace = getWorkspaceFromPath(pathname);

  // Initial redirect: If landing on root '/' with stored preference, redirect
  useEffect(() => {
    if (initialSyncDone) return;
    setInitialSyncDone(true);

    // Only redirect if on exact root path
    if (pathname === '/') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'production') {
        navigate(WORKSPACE_ROUTES.production, { replace: true });
      }
    }

    // Update localStorage to match current route
    localStorage.setItem(STORAGE_KEY, workspace);
  }, [pathname, workspace, navigate, initialSyncDone]);

  // Keep localStorage in sync with route changes (after initial sync)
  useEffect(() => {
    if (!initialSyncDone) return;
    localStorage.setItem(STORAGE_KEY, workspace);
  }, [workspace, initialSyncDone]);

  // User-initiated workspace change (from WorkspaceTabs)
  const setWorkspace = useCallback((newWorkspace: Workspace) => {
    if (newWorkspace === workspace) return;

    setTransitioning(true);
    localStorage.setItem(STORAGE_KEY, newWorkspace);
    navigate(WORKSPACE_ROUTES[newWorkspace]);

    // Clear transition state after animation
    setTimeout(() => setTransitioning(false), 300);
  }, [workspace, navigate]);

  return (
    <WorkspaceContext.Provider value={{ workspace, setWorkspace, transitioning }}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return context;
}
